import React, { useEffect, useRef } from 'react';

/**
 * SessionOverlay — thin absolute-positioned canvas that paints
 * translucent bands over the chart body for the three major FX/crypto
 * sessions (Asia / London / New York). Session windows use standard
 * industry ranges (UTC hours) so a Midwest-CT trader sees NY session
 * light up from 7 AM–4 PM local — their prime window.
 *
 *   Asia    00-08 UTC   (Tokyo open through overlap)
 *   London  07-16 UTC
 *   NY      13-22 UTC   (extended incl. after-hours)
 *
 * The two overlap bands (Asia/London and London/NY) receive a
 * combined tint automatically because the boxes stack.
 */
const SESSION_DEFS = [
  { key: 'asia',   label: 'ASIA',   startH: 0,  endH: 8,  color: 'rgba(0,229,255,0.06)' },
  { key: 'london', label: 'LONDON', startH: 7,  endH: 16, color: 'rgba(167,139,250,0.06)' },
  { key: 'ny',     label: 'NY',     startH: 13, endH: 22, color: 'rgba(0,255,136,0.06)' },
];

export default function SessionOverlay({ engine, enabled = false }) {
  const cnvRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!enabled || !engine?.provider?.chart) return;
    const chart = engine.provider.chart;
    const draw = () => {
      const cnv = cnvRef.current;
      const wrap = wrapRef.current;
      if (!cnv || !wrap) return;
      const rect = wrap.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      cnv.width = rect.width * dpr;
      cnv.height = rect.height * dpr;
      cnv.style.width = `${rect.width}px`;
      cnv.style.height = `${rect.height}px`;
      const ctx = cnv.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
      const ts = chart.timeScale();
      const visibleRange = ts.getVisibleRange();
      if (!visibleRange) return;
      const fromTs = visibleRange.from;
      const toTs = visibleRange.to;
      // Iterate calendar-day boundaries covered by the visible window
      // and paint each session's box for that day.
      const dayMs = 24 * 3600;
      // Start at the UTC midnight <= fromTs.
      const startDay = Math.floor(fromTs / dayMs) * dayMs;
      for (let dayTs = startDay; dayTs <= toTs; dayTs += dayMs) {
        for (const s of SESSION_DEFS) {
          const from = dayTs + s.startH * 3600;
          const to = dayTs + s.endH * 3600;
          if (to < fromTs || from > toTs) continue;
          const x1 = ts.timeToCoordinate(Math.max(from, fromTs));
          const x2 = ts.timeToCoordinate(Math.min(to, toTs));
          if (x1 == null || x2 == null) continue;
          ctx.fillStyle = s.color;
          ctx.fillRect(Math.min(x1, x2), 0, Math.abs(x2 - x1), rect.height);
        }
      }
    };
    draw();
    chart.timeScale().subscribeVisibleTimeRangeChange(draw);
    const obs = new ResizeObserver(draw);
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => {
      try { chart.timeScale().unsubscribeVisibleTimeRangeChange(draw); } catch { /* noop */ }
      obs.disconnect();
    };
  }, [engine, enabled]);

  if (!enabled) return null;
  return (
    <div ref={wrapRef} className="absolute inset-0 z-10 pointer-events-none" data-testid="crypto-session-overlay">
      <canvas ref={cnvRef} />
    </div>
  );
}
